import { EyeIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "../../../../components/Badge/Badge";
import Icon from "../../../../components/Icon";
import { buildDocumentUrl } from "../../../../handlers/documentStorage";
import { actions, docSubTypes } from "../../../../handlers/enums";
import useDSPProfileState from "../../../../hooks/use-DSPProfileState";
import { Button } from "../Button";
import DSPModal from "./DSPModal";

export { SMART_PROFILE_BASE_URL } from "../../../../handlers/documentStorage";

function DocumentsConfirmation({
	onPrev,
	smartApplication,
	onDSPChange,
	setShowDocument,
	setDocumentUrl,
	setLoadingDocument,
}) {
	const [showDSPModal, setShowDSPModal] = useState(false);
	const [documents, setDocuments] = useState(formatDocs(smartApplication.dsp.dsp_docs));
	const dspProfileState = useDSPProfileState();

	const dspModalData = dspProfileState.dspModalData(actions.UPDATE);

	function formatDocs(docs) {
		let formatedDocs;

		formatedDocs =
			docs === undefined || docs.length === 0
				? []
				: docs.map((doc) => {
						if (!Object.hasOwn(doc, "included")) {
							const _doc = { ...doc, included: false };
							if (_doc.doc_sub_type === docSubTypes.RESUME.name) _doc.included = true;
							return _doc;
						}

						return doc;
					});

		return formatedDocs;
	}

	const handleSelectedDoc = (doc) => {
		const _documents = [...documents];
		const docIndex = documents.findIndex((document) => document.doc_id === doc.doc_id);

		_documents[docIndex].included = !_documents[docIndex].included;
		setDocuments(_documents);
	};

	const handleDSPModalSelect = (option) => {
		dspProfileState.onDSPSelect(option);
		setShowDSPModal(false);
	};

	useEffect(() => {
		const _dsp = { ...smartApplication.dsp };
		_dsp.dsp_docs = documents.filter((doc) => doc.included === true);

		onDSPChange(_dsp);
	}, [documents]);

	return (
		<div className="mx-auto">
			<div className="flex justify-between">
				<h2 className="text-md font-inter-600 mb-4">
					Which Smart Profile (DSP) do you want to use for this application?
				</h2>
				<h2 className="text-sm font-bold font-inter mb-4 text-blue-500 cursor-pointer" onClick={() => onPrev()}>
					Change
				</h2>
			</div>
			<div className="space-y-2">
				<div className="border rounded-lg p-3 transition-all border-gray-300">
					<div className="flex justify-between items-start">
						<div>
							<h3 className="text-md font-inter font-bold"> {smartApplication.dsp.dsp_title} </h3>
							<p className="text-xs font-inter font-medium text-gray-500"> {smartApplication.email} </p>
							<ul className="mt-1 text-xs text-gray-700">
								{documents.map((doc) => (
									<li key={doc.doc_id} className="flex items-center gap-1">
										<Icon
											iconName={
												Object.values(docSubTypes).find((docSubType) => docSubType.name === doc.doc_sub_type).icon
											}
											size={15}
										/>
										<span>
											<span className="font-inter text-xs font-semibold text-gray-600">
												{Object.values(docSubTypes).find((docSubType) => docSubType.name === doc.doc_sub_type).name}:{" "}
											</span>
											<span className="font-inter text-xs font-medium text-gray-600">{` ${doc.file_name}`}</span>
											{doc.included && (
												<span className="inline-flex items-center ml-2 px-1.5 py-0.2 rounded-full text-[9px] font-medium bg-success-light text-success-default">
													Included
												</span>
											)}
										</span>
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			</div>

			{/* Documents List */}
			<div>
				<div className="mt-4 mb-4 flex justify-between">
					<span>
						<h3 className="text-md font-inter-600">My Documents</h3>
						<p className="text-sm font-inter font-medium text-gray-600">
							{`${
								documents.length > 0
									? "You can check or uncheck files to include them in your application"
									: "Use the Add button to add documents to your DSP before continuing with your application."
							}`}
						</p>
					</span>
					<h2
						className="text-sm font-bold font-inter mb-4 text-blue-500 cursor-pointer"
						onClick={() => setShowDSPModal(true)}
					>
						Add
					</h2>
				</div>

				<div className="space-y-2">
					{documents.map((doc) => (
						<div
							key={doc.doc_id}
							className="flex flex-col md:flex-row md:items-center md:justify-between p-3.5 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
						>
							<div className="flex items-center gap-3">
								<div className="bg-primary-50 rounded-md">
									<button
										id={doc.doc_id}
										disabled={doc.doc_sub_type === docSubTypes.RESUME.name}
										className="w-6 h-6 border flex items-center justify-center hover:opacity-80 transition-opacity"
										onClick={() => handleSelectedDoc(doc)}
										style={{
											background: "#f8f9fa",
											borderWidth: "2px",
											...(doc.included && { background: "#0A88ED", borderWidth: "none" }),
										}}
									>
										{doc.included && <i className="fa fa-check text-white" />}
									</button>
								</div>
								<div>
									<div className="flex flex-wrap align-items-start">
										<h4 className="text-[14px] font-medium text-gray-900 font-inter mb-0.5">{doc.file_name}</h4>
										<Badge
											variant={doc.included ? "success" : "danger"}
											className={`text-xs font-medium md:ml-2 ${doc.included ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"} border-0 rounded-md`}
										>
											{`${doc.included ? "Included" : "Not Included"}`} {doc.included}
										</Badge>
									</div>

									<div className="flex items-center gap-3">
										{/*<span className="text-xs text-gray-600">Uploaded on {doc.uploadDate}</span>*/}
										{/*<span className="text-xs text-gray-600">{doc.size}</span>*/}
										<span className="text-xs text-gray-600">Uploaded on 2024-11-08</span>
										<span className="text-xs text-gray-600">2.4 MB</span>
									</div>
								</div>
							</div>

							<div className="flex items-center gap-6">
								<Badge variant="secondary" className="text-xs font-medium bg-gray-100 text-gray-700">
									{Object.values(docSubTypes).find((docSubType) => docSubType.name === doc.doc_sub_type).name}
								</Badge>
								<div className="flex items-center gap-2">
									<Button
										variant="ghost"
										size="sm"
										title="View"
										className="h-8 w-8 p-0 hover:text-gray-1000"
										onClick={() => {
											setLoadingDocument(true);
											setDocumentUrl(buildDocumentUrl(doc.storage_key));
											setShowDocument((prev) => !prev);
										}}
									>
										<EyeIcon className="w-4 h-4 text-gray-600" />
										<span className="sr-only">View</span>
									</Button>
								</div>
							</div>
						</div>
					))}
				</div>

				<DSPModal
					data={dspModalData}
					isOpen={showDSPModal}
					action={actions.UPDATE}
					onSubmit={handleDSPModalSelect}
					onClose={() => setShowDSPModal(false)}
				/>
			</div>
		</div>
	);
}

export default DocumentsConfirmation;
